// prediction/wizards/ProductSetupWizard.jsx - 問題修正版
import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress
} from '@mui/material';

// 分離されたコンポーネントとユーティリティ
import { renderStepContent } from './SetupWizardSteps';
import {
  validateNumericInput,
  validateDate,
  validateStep,
  validateComplete,
  generateSteps,
  canProceedToNext,
  isCompleteDisabled,
  canSkipCurrentStep
} from './wizardUtils';

const ProductSetupWizard = ({ 
  open, 
  onClose, 
  onComplete,
  productName,
  clientId,
  productId,
  selectedProduct,
  showTankSetupStep = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tankSetupChoice, setTankSetupChoice] = useState(null);
  const [formData, setFormData] = useState({
    totalCapacity: '',
    initialConsumptionRate: '',
    actualRemaining: '',
    measuredDate: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});

  // 🚀 動的ステップ生成（ユーティリティから）
  const steps = useMemo(() => {
    const generatedSteps = generateSteps(showTankSetupStep, tankSetupChoice, selectedProduct);
    return generatedSteps;
  }, [showTankSetupStep, tankSetupChoice, selectedProduct]);

  // 🚀 進捗計算
  const getProgress = useCallback(() => {
    return Math.round(((currentStep + 1) / steps.length) * 100);
  }, [currentStep, steps.length]);

  // 🚀 入力変更処理（バリデーション統合）
  const handleInputChange = useCallback((fieldName, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setFormData(prevFormData => {
        const newFormData = { ...prevFormData, [fieldName]: value };
        
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          
          if (fieldName === 'totalCapacity') {
            const capacityError = validateNumericInput(value, 'タンク容量', true);
            if (capacityError) {
              newErrors.totalCapacity = capacityError;
            } else {
              delete newErrors.totalCapacity;
            }
            
            // 残量の再バリデーション
            if (prevFormData.actualRemaining) {
              const capacity = value ? parseInt(value, 10) : null;
              const remainingError = validateNumericInput(prevFormData.actualRemaining, '現在の残量', false, capacity);
              if (remainingError) {
                newErrors.actualRemaining = remainingError;
              } else {
                delete newErrors.actualRemaining;
              }
            }
          } else if (fieldName === 'actualRemaining') {
            const tankCapacity = prevFormData.totalCapacity ? parseInt(prevFormData.totalCapacity, 10) : null;
            const remainingError = validateNumericInput(value, '現在の残量', false, tankCapacity);
            if (remainingError) {
              newErrors.actualRemaining = remainingError;
            } else {
              delete newErrors.actualRemaining;
            }
          } else if (fieldName === 'initialConsumptionRate') {
            const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
            const isRequired = !hasTank;
            const consumptionError = validateNumericInput(value, '月間消費量', isRequired);
            if (consumptionError) {
              newErrors.initialConsumptionRate = consumptionError;
            } else {
              delete newErrors.initialConsumptionRate;
            }
          }
          
          return newErrors;
        });
        
        return newFormData;
      });
    }
  }, [tankSetupChoice, showTankSetupStep, selectedProduct]);

  // 🚀 日付変更処理
  const handleDateChange = useCallback((value) => {
    const today = new Date().toISOString().split('T')[0];
    if (value > today) {
      alert("未来の日付は登録できません。");
      setFormData(prev => ({ ...prev, measuredDate: today }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.measuredDate;
        return newErrors;
      });
    } else {
      setFormData(prev => ({ ...prev, measuredDate: value }));
      const dateError = validateDate(value);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (dateError) {
          newErrors.measuredDate = dateError;
        } else {
          delete newErrors.measuredDate;
        }
        return newErrors;
      });
    }
  }, []);

  // 🚀 タンク設置選択変更
  const handleTankSetupChange = useCallback((choice) => {
    setTankSetupChoice(choice);
  }, []);

  // 🚀 ステップ移動
  const handleNext = useCallback(() => {
    const stepErrors = validateStep(steps[currentStep].id, formData, tankSetupChoice, selectedProduct, showTankSetupStep);
    setErrors(stepErrors);
    
    if (Object.keys(stepErrors).length === 0) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps, formData, tankSetupChoice, selectedProduct, showTankSetupStep]);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  // 🚀 完了処理 - 機能フラグ自動設定対応
  const handleComplete = useCallback(async () => {
    try {
      // 最終ステップのバリデーション
      const stepErrors = validateStep(steps[currentStep].id, formData, tankSetupChoice, selectedProduct, showTankSetupStep);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        return;
      }

      // 全体バリデーション
      const completeError = validateComplete(formData, tankSetupChoice, showTankSetupStep, selectedProduct);
      if (completeError) {
        alert(completeError);
        return;
      }

      const payload = {
        clientId,
        productId
      };

      // 🚀 修正：タンク設置選択に応じた機能フラグ設定
      if (showTankSetupStep) {
        // 新規顧客の場合：ユーザーの選択を反映
        payload.tankInstalled = Boolean(tankSetupChoice === true);  // 🔧 明示的なBoolean変換
        payload.predictionEnabled = true;         // 🚀 ウィザード完了 = 予測機能有効
      } else {
        // 既存顧客の場合：FunctionControlDialogでの設定を保持
        const currentTankSetting = Boolean(selectedProduct?.tankInstalled === true);
        payload.tankInstalled = currentTankSetting;  // 🔧 FunctionControlDialogでの設定を保持
        payload.predictionEnabled = true; // 🚀 ウィザード完了 = 予測機能有効
      }

      // タンク容量の追加（タンク設置選択時のみ）
      if (payload.tankInstalled && formData.totalCapacity) {
        payload.totalCapacity = parseInt(formData.totalCapacity, 10);
      }

      // 消費量の追加
      if (formData.initialConsumptionRate && formData.initialConsumptionRate.trim() !== '') {
        const consumptionValue = parseInt(formData.initialConsumptionRate, 10);
        if (!isNaN(consumptionValue) && consumptionValue > 0) {
          payload.initialConsumptionRate = consumptionValue;
        }
      }

      // 実測残量と測定日の追加処理
      if (formData.actualRemaining && formData.actualRemaining.trim() !== '') {
        const remainingValue = parseInt(formData.actualRemaining, 10);
        if (!isNaN(remainingValue) && remainingValue >= 0) {
          payload.actualRemaining = remainingValue;
          // 測定日も必ず含める
          payload.measuredDate = formData.measuredDate || new Date().toISOString().split('T')[0];
        }
      }

      await onComplete(payload);
      
    } catch (error) {
      let message = "製品設定の作成に失敗しました。もう一度お試しください。";
      if (error.response?.data && typeof error.response.data === "string") {
        message = error.response.data;
      } else if (error.message) {
        message = `エラー: ${error.message}`;
      }
      alert(message);
    }
  }, [currentStep, steps, formData, tankSetupChoice, clientId, productId, showTankSetupStep, selectedProduct, onComplete]);

  // 🚀 各種判定（ユーティリティから）
  const canProceed = useMemo(() => {
    return canProceedToNext(currentStep, steps, tankSetupChoice, formData, errors, showTankSetupStep, selectedProduct);
  }, [currentStep, steps, tankSetupChoice, formData, errors, showTankSetupStep, selectedProduct]);

  const completeDisabled = useMemo(() => {
    return isCompleteDisabled(errors, showTankSetupStep, tankSetupChoice, selectedProduct, formData);
  }, [errors, showTankSetupStep, tankSetupChoice, selectedProduct, formData]);

  const canSkip = useMemo(() => 
    canSkipCurrentStep(currentStep, steps, showTankSetupStep, tankSetupChoice, selectedProduct),
    [currentStep, steps, showTankSetupStep, tankSetupChoice, selectedProduct]
  );

  const isLastStep = currentStep === steps.length - 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h5" gutterBottom>
            {productName || '製品'} 予測機能設定
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              {currentStep + 1} / {steps.length}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={getProgress()} 
              sx={{ flexGrow: 1 }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {currentStep === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>予測機能の設定</strong><br/>
              {showTankSetupStep ? 
                'タンク設置状況に応じて最適な設定を行います' :
                '残量予測に必要な情報を設定します'
              }
            </Typography>
          </Alert>
        )}

        <Box sx={{ minHeight: 300 }}>
          {steps[currentStep] && renderStepContent(
            steps[currentStep],
            formData,
            tankSetupChoice,
            handleTankSetupChange,
            handleInputChange,
            handleDateChange,
            errors,
            showTankSetupStep,
            selectedProduct
          )}
        </Box>

        {isLastStep && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              ✅ 設定完了！予測機能が利用できます
            </Typography>
          </Alert>
        )}
      </DialogContent>

      {/* 🚀 統一されたボタンスタイル */}
      <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
        {/* プライマリアクション：最重要 */}
        {!isLastStep ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed}
            size="large"
            fullWidth
            sx={{ order: 1 }}
          >
            次へ
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleComplete}
            color="primary"
            disabled={completeDisabled}
            size="large"
            fullWidth
            sx={{ order: 1 }}
          >
            設定完了
          </Button>
        )}
        
        {/* セカンダリアクション：重要だが補助的 */}
        {currentStep > 0 && (
          <Button 
            variant="outlined"
            onClick={handleBack}
            size="large"
            fullWidth
            sx={{ order: 2 }}
          >
            戻る
          </Button>
        )}
        
        {/* オプション系アクション：水平配置で統一 */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          width: '100%', 
          justifyContent: 'center',
          order: 3 
        }}>
          {canSkip && !isLastStep && (
            <Button
              variant="text"
              onClick={() => {
                setCurrentStep(currentStep + 1);
              }}
              size="medium"
              sx={{ minWidth: 100 }}
            >
              スキップ
            </Button>
          )}
          
          <Button 
            variant="text"
            onClick={() => {
              onClose();
            }}
            color="inherit"
            size="medium"
            sx={{ minWidth: 100 }}
          >
            キャンセル
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSetupWizard;